import { TableProps as AntdTableProps, Table as AntdTable } from "antd";
import { ColumnType } from "antd/lib/table";
import { Component, Children, cloneElement, ReactNode, MouseEventHandler } from "react";
import { TableWidthPayload, tableWidthStore } from "./TableWidthStore";
import classNames from "classnames";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface TableProps<T = any> extends AntdTableProps<T> {
  /**
   * Unique name of the table. Used to store the user's personal column widths in the localStorage.
   * Make sure to check if the name is unique, otherwise the error will be thrown.
   */
  name: string;
}

interface ResizableTitleState {
  width: number;
  oldX: number;
  isMouseDown: boolean;
}

interface ResizableTitleProps {
  width?: number;
  children: ReactNode;
  onResize: (width: number) => void;
}

class ResizableTitle extends Component<ResizableTitleProps, ResizableTitleState> {
  private draggerParentNode: HTMLElement = null;
  constructor(props: ResizableTitleProps) {
    super(props);
    this.state = {
      width: 0,
      oldX: 0,
      isMouseDown: false,
    };
  }

  handleMouseDown: MouseEventHandler<HTMLSpanElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const targetParent = e.currentTarget.parentElement;
    // disabling pointer-events is required to not fire click event after releasing the resizer
    targetParent.style.pointerEvents = "none";
    this.draggerParentNode = targetParent;
    const offsetWidth = targetParent.offsetWidth;
    this.setState(
      {
        oldX: e.pageX,
        width: offsetWidth,
        isMouseDown: true,
      },
      () => {
        window.addEventListener("mouseup", this.handleMouseUp, false);
        window.addEventListener("mousemove", this.handleMouseMove, false);
      }
    );
  };

  handleMouseUp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.draggerParentNode.style.pointerEvents = "";

    this.setState({
      oldX: 0,
      width: 0,
      isMouseDown: false,
    });
    window.removeEventListener("mouseup", this.handleMouseUp, false);
    window.removeEventListener("mousemove", this.handleMouseMove, false);
  };

  handleMouseMove = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const widthDiff = e.pageX - this.state.oldX;
    let newWidth = this.state.width + widthDiff;
    if (widthDiff < 0) {
      newWidth -= 10;
    }
    if (newWidth > 50) {
      this.props.onResize(newWidth);
    } else {
      this.props.onResize(50);
    }
  };

  render() {
    const { children, onResize: _, width: _width, ...restProps } = this.props;
    return (
      <th
        {...restProps}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        // onMouseUp={(e) => {
        //   e.preventDefault();
        //   e.stopPropagation();
        // }}
      >
        {children}
        <span className="antd-table-resizable-handle" onMouseDown={this.handleMouseDown} />
      </th>
    );
  }
}

interface TableState {
  localColumns: TableProps["columns"];
  localChildren: ReactNode[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class Table<T = any> extends Component<TableProps<T>, TableState> {
  constructor(props: TableProps) {
    super(props);

    this.state = {
      localColumns: this.mapColumnProps(props.columns || []),
      localChildren: null,
    };
  }

  componentDidMount(): void {
    this.setState({
      localChildren: this.mapChildrenProps(this.props.children),
      localColumns: this.mapColumnProps(this.props.columns),
    });
  }

  componentDidUpdate(_prevProps: Readonly<TableProps>): void {
    if (this.props.columns?.length !== this.state.localColumns?.length) {
      this.setState({ localColumns: this.mapColumnProps(this.props.columns) });
    }
  }

  handleResizeByColumns = (index: number) => (width) => {
    const nextColumns = this.state.localColumns.slice();
    nextColumns[index] = {
      ...nextColumns[index],
      width: width,
    };
    this.setState({ localColumns: nextColumns });
    this.saveWidths(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      nextColumns.map((i) => ({ dataIndex: (i as ColumnType<any>).dataIndex as string, width: i.width as number }))
    );
  };

  handleResizeByChildren = (index: number) => (width) => {
    const nextChildren = this.state.localChildren.slice();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nextChildren[index] = cloneElement(nextChildren[index] as any, { width: width });
    this.setState({ localChildren: nextChildren });
    this.saveWidths(
      nextChildren.map((i) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const props = (i as any).props as ColumnType<any>;
        return {
          dataIndex: props.dataIndex as string,
          width: props.width as number,
        };
      })
    );
  };

  saveWidths = (payload: TableWidthPayload) => {
    tableWidthStore.set(this.props.name, payload);
  };

  mapColumnProps = (columnsProps: TableProps["columns"]): TableProps["columns"] => {
    const config = tableWidthStore.get(this.props.name) || [];
    const configMap = config.reduce((result, item) => {
      result[item.dataIndex] = item.width;
      return result;
    }, {});
    return columnsProps?.map((col, index) => ({
      ...col,
      width: configMap[(col as ColumnType<unknown>).dataIndex as string] || col.width,
      onHeaderCell: (column) => ({
        width: (column as ColumnType<unknown>).width,
        onResize: this.handleResizeByColumns(index),
      }),
    })) as TableProps["columns"];
  };

  mapChildrenProps = (children) => {
    const config = tableWidthStore.get(this.props.name) || [];
    const configMap = config.reduce((result, item) => {
      result[item.dataIndex] = item.width;
      return result;
    }, {});
    return Children.map(children, (child, index) => {
      if (child) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return cloneElement(child as any, {
          key: index,
          width: configMap[child.props.dataIndex] || child.props.width,
          onHeaderCell: (column) => ({
            width: (column as ColumnType<unknown>).width,
            onResize: this.handleResizeByChildren(index),
          }),
        });
      } else {
        return child;
      }
    });
  };

  /* Check if this getter doesn't make any performance issues. 
  If not - remove the localColumns, localChildren and all its stuff handling in component state */
  get columns() {
    return this.mapColumnProps(this.props.columns);
  }

  render() {
    return (
      <AntdTable
        {...this.props}
        className={classNames("app-table", this.props.className)}
        components={{ ...this.props.components, header: { ...this.props.components?.header, cell: ResizableTitle } }}
        columns={this.columns}
      >
        {this.state.localChildren}
      </AntdTable>
    );
  }
}
